import { LatLngBoundsExpression } from 'leaflet';
import { useEffect, useState } from 'react';
import { DEFAULT_CHOROPLETH_MAP_COLORS, DEFAULT_FILL_COLOR } from '@common/constants';
import { ByZctaCollection, UseUSMapChoroplethProps } from '@common/types';
import { buildBbox, getStdDeviation } from '@common/utils';
import { MapFeatureConfig, UseUSMapChoroplethValue } from '../../../../../types/maps.types';
import { Feature } from 'geojson';
import usZctasJson from '../assets/usa_zip_codes_geo_15m.json';

const useUSMapChoropleth = <T extends ByZctaCollection>({
  zctas,
}: UseUSMapChoroplethProps<T>): UseUSMapChoroplethValue => {
  const usZctas = (usZctasJson as { features: Feature[] }).features;
  const [bounds, setBounds] = useState<LatLngBoundsExpression | undefined>();
  const [featuresList, setFeaturesList] = useState<MapFeatureConfig[]>([]);
  const colors = DEFAULT_CHOROPLETH_MAP_COLORS;

  const getColorFromValue = (value: number, colorRanges: number[]): string | undefined => {
    const getColorByValue = (i = 0): string | undefined => {
      i = Math.min(i, colors.length);

      const minBound = colorRanges[i];
      const maxBound = colorRanges[i + 1];

      return value >= minBound && value <= maxBound ? colors[i] : getColorByValue(i + 1);
    };

    return getColorByValue();
  };

  useEffect(() => {
    if (!usZctas) return;

    // This is shallow copy. This will require to clean the original object
    const zctasFeatures = [...usZctas];

    // Cleaning previous values as the above is a shallow copy
    zctasFeatures.forEach((feature) => {
      if (feature?.properties?.value) {
        delete feature.properties.value;
        delete feature.properties.fillColor;
      }
    });

    const totalConsentsByState = new Array(100).fill(0, 0, 100);

    // Adding consents to the state (json with geometries)
    // if the geometry has properties and the zcta is in the zctas list
    zctasFeatures.forEach((feature: Feature) => {
      if (zctas && feature.properties) {
        const zipCode = feature.properties.ZCTA5CE10;
        const zcta = zctas.find((zcta) => zcta.id === zipCode);
        if (zcta) {
          feature.properties.value = zcta.value;
          feature.id = zcta.id;
          const state = parseInt(feature.properties.STATEFP10);
          totalConsentsByState[state] += zcta.value;
        }
      }
    });

    // Getting state index with maximum consents
    const biggestConsentsState = totalConsentsByState.reduce((biggestState, value, index) => {
      return value > totalConsentsByState[biggestState] ? index : biggestState;
    }, 0);

    // Removing geometry without value
    const selectedZctas = zctasFeatures.filter((feature) => !!feature.properties?.value) ?? [];
    const selectedValues: number[] = selectedZctas?.map((f) => f?.properties?.value ?? 0);

    let zctasColorRanges: number[] = [];
    if (selectedValues.length) {
      zctasColorRanges = getStdDeviation(selectedValues, colors.length);
    }

    // cleaning geometries with no properties and adding fillColor
    selectedZctas.forEach((zcta) => {
      zcta.properties = zcta.properties ?? {};
      zcta.properties.fillColor = zcta?.properties?.value
        ? getColorFromValue(zcta?.properties?.value, zctasColorRanges)
        : DEFAULT_FILL_COLOR;
    });

    const highestDensityStateFeatures = selectedZctas.filter(
      (feature) => parseInt(feature.properties?.STATEFP10) === biggestConsentsState,
    );

    if (selectedZctas.length) setBounds(buildBbox(highestDensityStateFeatures));

    setFeaturesList([
      {
        features: usZctas,
        markers: false,
        fillColor: DEFAULT_FILL_COLOR,
        name: 'usZctas',
        type: 'FeatureCollection',
      },
      {
        type: 'FeatureCollection',
        features: selectedZctas,
        markers: true,
        fillColor: DEFAULT_FILL_COLOR,
        colorRanges: zctasColorRanges,
        colors,
        name: 'selectedZctas',
      },
    ]);
  }, [zctas, usZctas]);

  return {
    featuresList,
    bounds,
  };
};

export default useUSMapChoropleth;
